import { EnumerationsLogic } from "../EnumerationsLogic";
import { Calculation, CalculationListItem } from "../../model";
import { JsonSchemaCache } from "../../util";
import { Translations } from "../Translations";
import { DataMapper } from "./DataMapper";
import { DataMapperInfo } from "./DataMapperInfo";
import { DataMapperSelector } from "./DataMapperSelector";
import { CalculationsDataRow } from "./data/CalculationsDataRow";

/**
 * Provides in-built logc for data mappers.
 */
export class DataMappersLogic {

  /**
   * Gets the MapperInfo from a full DataMapper.
   * @param mapper The full mapper.
   * @returns The info part of the mapper.
   */
  public static getInfo(mapper: DataMapper<any, any>): DataMapperInfo | undefined {
    if (!mapper) {
      return undefined;
    }
    return {
      sourceSchema: mapper.source.name,
      targetSchema: mapper.target.name,
      title: mapper.title,
      uri: mapper.uri,
    };
  }

  /** Gets the mappers for specific sources / targets. */
  public static getMappers(sourceFilter?: string[], targetFilter?: string[]): DataMapperInfo[] {
    return this.getBuiltInMappers().filter((mapper) =>
      (!sourceFilter?.length || sourceFilter.find(x => x == mapper.sourceSchema)) &&
      (!targetFilter?.length || targetFilter.find(x => x == mapper.targetSchema))
    );
  }

  /** Gets the listing of all in-built data mappers in @salaxy/core library. */
  public static getBuiltInMappers(): DataMapperInfo[] {
    return [
      {
        uri: "@salaxy/core/WorkerListItem_Default",
        title: Translations.get("SALAXY.CORE.WorkerMappers.preview"),
        sourceSchema: "WorkerListItem",
        targetSchema: undefined,
      },
      {
        uri: "@salaxy/core/WorkerObjectExport",
        title: Translations.get("SALAXY.CORE.WorkerMappers.defaultMaterial") + " (export)",
        sourceSchema: "WorkerAccount",
        targetSchema: "WorkersDataRow",
      },
      {
        uri: "@salaxy/core/EmploymentListItem_Default",
        title: Translations.get("SALAXY.CORE.WorkerMappers.preview"),
        sourceSchema: "EmploymentListItem",
        targetSchema: undefined,
      },
      {
        uri: "@salaxy/core/EmploymentExport",
        title: Translations.get("SALAXY.CORE.WorkerMappers.defaultMaterial") + " (export)",
        sourceSchema: "Employment",
        targetSchema: "WorkersDataRow",
      },
      {
        uri: "@salaxy/core/CalculationExport",
        title: Translations.get("SALAXY.CORE.CalculationMappers.defaultMaterial") + " (export)",
        sourceSchema: "Calculation",
        targetSchema: "CalculationsDataRow",
      },
      {
        uri: "@salaxy/core/CalculationListItem_Default",
        title: Translations.get("SALAXY.CORE.CalculationMappers.preview"),
        sourceSchema: "CalculationListItem",
        targetSchema: undefined,
      },
      {
        uri: "@salaxy/core/CalculationListItem_Pro",
        title: "PRO Export",
        sourceSchema: "CalculationListItem",
        targetSchema: undefined,
      }
    ];
  }

  /**
   * Gets an export DataMapper, potentially loading the schema / mapping from the storage.
   * @param id Identifier or URI for the mapper.
   * URI starts with "@salaxy/core/", e.g. "@salaxy/core/Calculation_Default".
   * Id is just the last part of the path, e.g. "Calculation_Default".
   * @returns The mapper that maps the source to target. Null if none is found.
   */
  public static async getBuiltInMapper(id: string, schemaCache: JsonSchemaCache): Promise<DataMapper<any, any> | undefined> {
    if (!id) {
      return undefined;
    }
    id = this.getMapperUri(id);
    switch (id) {
      case "EmploymentExport": {
        const paths = [
          "id",
          "personalInfo.avatar.firstName",
          "personalInfo.avatar.lastName",
          "personalInfo.email",
          "personalInfo.telephone",
          "personalInfo.ibanNumber",
          "personalInfo.officialId",
          "info.startDate",
          "info.endDate",
          "info.isFixedTerm",
          "info.taxcard",
          "info.type",
          "info.pensionCalculation",
          "work.occupationCode",
          "info.irIncomeEarnerTypes",
          "isRegistered",
          "info.workerAccountId",
          "holidays.code",
          "holidays.bonusMethod",
          "updatedAt",
          "createdAt",
        ];
        const mapper = new DataMapper<any, any>("EmploymentExport", Translations.get("SALAXY.CORE.WorkerMappers.defaultMaterial") + " (export)");
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "Employment");
        mapper.setPathsFromSourceSchema(paths);
        return mapper;
      }
      case "EmploymentListItem_Default": {
        const paths = [
          "otherPartyInfo.avatar.firstName",
          "otherPartyInfo.avatar.lastName",
          "otherPartyInfo.email",
          "otherPartyInfo.telephone",
          "otherPartyInfo.ibanNumber",
          "otherPartyInfo.officialId",
          "data.startDate",
          "data.endDate",
          "data.isFixedTerm",
          "data.taxcard",
          "data.type",
          "data.pensionCalculation",
        ];
        const mapper = new DataMapper<any, any>("EmploymentListItem_Default", Translations.get("SALAXY.CORE.WorkerMappers.preview"));
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "EmploymentListItem");
        mapper.setPathsFromSourceSchema(paths);
        mapper.isSourceAList = true;
        return mapper;
      }
      case "WorkerObjectExport": {
        const paths = [
          "id",
          "avatar.firstName",
          "avatar.lastName",
          "contact.email",
          "contact.telephone",
          "ibanNumber",
          "officialId",
        ];
        const mapper = new DataMapper<any, any>("WorkerObjectExport", Translations.get("SALAXY.CORE.WorkerMappers.defaultMaterial") + " (export)");
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "WorkerAccount");
        mapper.setPathsFromSourceSchema(paths);
        return mapper;
      }
      case "WorkerListItem_Default": {
        const paths = [
          "otherPartyInfo.avatar.firstName",
          "otherPartyInfo.avatar.lastName",
          "otherPartyInfo.email",
          "otherPartyInfo.telephone",
          "otherPartyInfo.ibanNumber",
          "otherPartyInfo.officialId"];
        const mapper = new DataMapper<any, any>("WorkerListItem_Default", Translations.get("SALAXY.CORE.WorkerMappers.preview"));
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "WorkerListItem");
        mapper.setPathsFromSourceSchema(paths);
        mapper.isSourceAList = true;
        return mapper;
      }
      case "CalculationListItem_Default": {
        const paths = [
          "status",
          "otherPartyInfo.avatar.displayName",
          "otherPartyInfo.officialId",
          "data.workStartDate",
          "data.workEndDate",
          "shortText",
          "salaryDate",
          "data.workerPayment",
          "data.taxPayment",
          "grossSalary"];
        const mapper = new DataMapper<any, any>("CalculationListItem_Default", Translations.get("SALAXY.CORE.CalculationMappers.preview"));
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "CalculationListItem");
        mapper.setPathsFromSourceSchema(paths);
        mapper.isSourceAList = true;
        return mapper;
      }
      case "CalculationListItem_Pro": {
        const mapper = new DataMapper("CalculationListItem_Pro", "PRO export");
        mapper.isSourceAList = true;
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "CalculationListItem");
        const map = {
          "ownerInfo.avatar.displayName": Translations.get("SALAXY.NG1_Other.Roles.employer"),
          "ownerInfo.officialId": Translations.get("SALAXY.NG1_Other.Roles.employer") + " ID",
          "status": new DataMapperSelector(undefined, Translations.get("SALAXY.MODEL.CalculationListItem.status.title"),
            (source: CalculationListItem) => EnumerationsLogic.getEnumLabel("CalculationStatus", source.status ?? "")
          ),
          "otherPartyInfo.avatar.displayName": Translations.get("SALAXY.NG1_Other.Roles.worker"),
          "otherPartyInfo.officialId": Translations.get("SALAXY.NG1_Other.Roles.worker") + " ID",
          "data.workStartDate": Translations.get("SALAXY.MODEL.CalculationData.workStartDate.title"),
          "data.workEndDate": Translations.get("SALAXY.MODEL.CalculationData.workEndDate.title"),
          "shortText": Translations.get("SALAXY.MODEL.CalculationListItem.shortText.title"),
          "salaryDate": Translations.get("SALAXY.MODEL.CalculationListItem.salaryDate.title"),
          "data.workerPayment": Translations.get("SALAXY.MODEL.CalculationData.workerPayment.title"),
          "data.taxPayment": Translations.get("SALAXY.MODEL.CalculationData.taxPayment.title"),
          "grossSalary": Translations.get("SALAXY.MODEL.CalculationListItem.grossSalary.title"),
        };
        mapper.setPaths(map);
        mapper.generateSchema("CalculationListItem_Pro", "target");
        mapper.isSourceAList = true;
        return mapper;
      }
      case "CalculationExport": {

        const rowSelector = new DataMapperSelector<Calculation, CalculationsDataRow>("rows");
        rowSelector.flatten = [
          new DataMapperSelector<Calculation, CalculationsDataRow>("rowType", "rowType"),
          new DataMapperSelector<Calculation, CalculationsDataRow>("message", "message"),
          new DataMapperSelector<Calculation, CalculationsDataRow>("count", "count"),
          new DataMapperSelector<Calculation, CalculationsDataRow>("price", "price"),
        ];

        const paths = {
          "id": "id",
          "workflow.status": "status",
          "worker.paymentData.socialSecurityNumber": "personalId",
          "worker.avatar.firstName": "firstName",
          "worker.avatar.lastName": "lastName",
          "worker.paymentData.email": "email",
          "worker.paymentData.telephone": "telephone",
          "worker.paymentData.ibanNumber": "ibanNumber",
          "info.occupationCode": "occupationCode",
          "info.workStartDate": "periodStart",
          "info.workEndDate": "periodEnd",
          "info.workDescription": "workDescription",
          "info.salarySlipMessage": "salarySlipMessage",
          "info.costCenter": "costCenter",
          "workflow.salaryDate": "salaryDate",
          "worker.tax.fixedTaxAmount": "fixedTaxAmount",
          "rows": rowSelector,
        };
        const mapper = new DataMapper<Calculation, CalculationsDataRow>("CalculationExport", Translations.get("SALAXY.CORE.WorkerMappers.defaultMaterial") + " (export)");
        await mapper.setSchemaFromCache(schemaCache, "salaxy-secure/schemas/v02.json", "Calculation", "source");
        mapper.setPaths(paths);
        mapper.generateSchema("CalculationsDataRow", "target");
        return mapper;
      }
      default:
        return undefined;
    }
  }

  /** TODO: try to figure out why this is required? */
  public static getMapperUri(uri: string): string {
    if (!uri) {
      return uri;
    }
    return uri.replace("@salaxy/core/", "");
  }
}